import React, { useState, useMemo, useEffect } from 'react';
import { Button, Modal, Select, Checkbox, Alert, Space, Typography } from 'antd';
import { ALL_BLUEPRINTS, getRecommendedBlueprint, resolveSegment, getStrategySetOptions } from '../../../../core/strategyBlueprint/blueprints';
import { useTimezone } from '../../../../core/hooks/useTimezone';
import { toStartOfDayUtc, toEndOfDayUtc } from '../../CampaignRequestor/utils/dateNormalization';

const { Text } = Typography;

// Module-level helpers — no component state needed
function resolveBlueprint(key) {
  return ALL_BLUEPRINTS.find((b) => b.key === key) || null;
}

function getOptionsForBlueprint(blueprintKey) {
  const bp = resolveBlueprint(blueprintKey);
  return getStrategySetOptions(bp);
}

function buildEnabledItems(set) {
  if (!set || !set.lineItems) return {};
  const map = {};
  set.lineItems.forEach((_, i) => { map[i] = true; });
  return map;
}

function computeItemBudget(set, enabledItems, sourceBudget, blueprintIndex) {
  const enabledWeight = set.lineItems.reduce(
    (sum, bi, i) => (enabledItems[i] ? sum + (Number(bi.budgetAllocation) || 0) : sum), 0,
  );
  if (enabledWeight === 0) return 0;
  const weight = Number(set.lineItems[blueprintIndex].budgetAllocation) || 0;
  return Math.round((weight / enabledWeight) * sourceBudget * 100) / 100;
}

export function StrategyBlueprintModal({ form, campaignIndex, currentCampaignOrder, visible, onClose, onFormUpdated }) {
  const { timezone } = useTimezone();
  const [assignments, setAssignments] = useState({});
  const [markOriginals, setMarkOriginals] = useState(true);

  const advertiser = currentCampaignOrder?.advertiser;
  const parentOrgs = Array.isArray(advertiser?.parentOrg) ? advertiser.parentOrg : advertiser?.parentOrg ? [advertiser.parentOrg] : [];
  const channelPartnerName = parentOrgs.map((p) => p.name).join(' ') || advertiser?.name || '';
  const cpAcronym = parentOrgs.map((p) => p.CPAcronym).filter(Boolean).join(' ') || advertiser?.CPAcronym || '';
  const campaignBudget = currentCampaignOrder?.campaigns?.[campaignIndex]?.budgetTotal || currentCampaignOrder?.budget || 0;

  const recommendedBlueprint = useMemo(
    () => getRecommendedBlueprint(channelPartnerName, cpAcronym, campaignBudget),
    [channelPartnerName, cpAcronym, campaignBudget],
  );

  const originalLineItems = useMemo(() => {
    const items = form.getFieldValue(['campaigns', campaignIndex, 'lineItems']) || [];
    return items;
  }, [form, campaignIndex]);

  // Reset assignments when modal opens — each item gets its own blueprint key
  useEffect(() => {
    if (visible) {
      const defaultKey = recommendedBlueprint?.key || ALL_BLUEPRINTS[0]?.key || null;
      const defaultOpts = getOptionsForBlueprint(defaultKey);
      const defaultBp = resolveBlueprint(defaultKey);
      const defaultSet = defaultBp?.strategySets?.[defaultOpts[0]?.key];
      const initial = {};
      originalLineItems.forEach((_, idx) => {
        initial[idx] = { blueprintKey: defaultKey, strategyKey: defaultOpts[0]?.key || null, skip: false, enabledItems: buildEnabledItems(defaultSet) };
      });
      setAssignments(initial);
      setMarkOriginals(true);
    }
  }, [visible, originalLineItems.length, originalLineItems, recommendedBlueprint]);

  const totals = useMemo(() => {
    let totalItems = 0;
    originalLineItems.forEach((li, idx) => {
      const assignment = assignments[idx];
      if (!assignment || assignment.skip || !assignment.strategyKey || !assignment.blueprintKey) return;
      const bp = resolveBlueprint(assignment.blueprintKey);
      if (!bp) return;
      const set = bp.strategySets[assignment.strategyKey];
      if (!set) return;
      const enabled = assignment.enabledItems || {};
      totalItems += set.lineItems.filter((_, i) => enabled[i] !== false).length;
    });
    return { totalItems, totalBudget: campaignBudget };
  }, [assignments, originalLineItems, campaignBudget]);

  const handleApply = () => {
    // Use the campaign's budget as the total pool
    const budgetField = form.getFieldValue(['campaigns', campaignIndex, 'budget']);
    const campaignBudget = Number(budgetField) || 0;

    const allClones = [];

    originalLineItems.forEach((sourceItem, idx) => {
      const assignment = assignments[idx];
      if (!assignment || assignment.skip || !assignment.strategyKey || !assignment.blueprintKey) return;

      const blueprint = resolveBlueprint(assignment.blueprintKey);
      if (!blueprint) return;

      const set = blueprint.strategySets[assignment.strategyKey];
      if (!set) return;

      const campaignStartDate = form.getFieldValue(['campaigns', campaignIndex, 'startDate']);
      const campaignEndDate = form.getFieldValue(['campaigns', campaignIndex, 'endDate']);

      // Only iterate enabled blueprint items
      const enabledItems = assignment.enabledItems || {};
      const enabledIndices = set.lineItems.map((_, i) => i).filter((i) => enabledItems[i] !== false);
      if (enabledIndices.length === 0) return;

      // Calculate total weight over enabled items only
      const setTotalWeight = enabledIndices.reduce(
        (sum, i) => sum + (Number(set.lineItems[i].budgetAllocation) || 0), 0,
      );
      const sourceBudget = Number(sourceItem.budgetTotal) || 0;

      let setAllocated = 0;

      enabledIndices.forEach((blueprintIndex, enabledPosition) => {
        const blueprintItem = set.lineItems[blueprintIndex];
        const clone = JSON.parse(JSON.stringify(sourceItem));
        delete clone.id;

        clone.name = `${blueprintItem.name}_${sourceItem.name}`;
        // Clear inherited budgetPercentage — the clone's budgetTotal no longer matches
        // the source's percentage. Leaving it stale causes onBlur to overwrite budgetTotal.
        clone.budgetPercentage = null;
        // Use source line item's budget as the pool, distributed by relative weight
        const weight = Number(blueprintItem.budgetAllocation) || 0;
        clone.budgetTotal = setTotalWeight > 0
          ? Math.round((weight / setTotalWeight) * sourceBudget * 100) / 100
          : 0;

        // Fix rounding on last enabled clone so the set sums exactly to source budget
        if (enabledPosition === enabledIndices.length - 1 && setTotalWeight > 0) {
          clone.budgetTotal = Math.round((sourceBudget - setAllocated) * 100) / 100;
        }
        setAllocated += clone.budgetTotal;

        // Recalculate budgetPercentage from the new budgetTotal so it stays consistent
        if (campaignBudget > 0 && clone.budgetTotal > 0) {
          clone.budgetPercentage = parseFloat((clone.budgetTotal / campaignBudget * 100).toFixed(1));
        }

        clone.isOrigin = false;
        clone.markedForDelivery = true;
        clone.valueAdd = false;
        clone.dspLineItemId = null;

        // Clean up nested IDs — same as manual clone button
        // Without this, all blueprint clones share the same adGroup/media records
        delete clone.adProviderId;
        if (clone.adGroup && clone.adGroup.id) {
          delete clone.adGroup.id;
          if (clone.adGroup.adGroupCreatives && clone.adGroup.adGroupCreatives.length) {
            clone.adGroup.adGroupCreatives = clone.adGroup.adGroupCreatives.map((agc) => {
              delete agc.id;
              delete agc.adProviderId;
              // Re-hydrate creative dates (JSON.stringify turned Moments into strings)
              agc.startDate = toStartOfDayUtc(agc.startDate, timezone);
              agc.endDate = toEndOfDayUtc(agc.endDate, timezone);
              if (agc.medias && agc.medias.length) {
                agc.medias = agc.medias.map((media) => {
                  delete media.id;
                  delete media.dspCreativeId;
                  delete media.dspAdId;
                  return media;
                });
              }
              return agc;
            });
          }
        }
        if (clone.firstPartyDataAudiences && clone.firstPartyDataAudiences.length) {
          clone.firstPartyDataAudiences.forEach((a) => delete a.id);
        }
        if (clone.targetingRegions && clone.targetingRegions.length) {
          clone.targetingRegions.forEach((r) => delete r.id);
        }
        if (clone.targetingLocations && clone.targetingLocations.length) {
          clone.targetingLocations.forEach((l) => delete l.id);
        }

        // Re-hydrate flight schedule dates: JSON.stringify turned Moment objects into strings.
        // AntD DatePicker expects Moment objects, so we normalize them back.
        const rawFlights = clone.flightSchedule || clone.flightSchedules;
        if (Array.isArray(rawFlights) && rawFlights.length > 0) {
          clone.flightSchedule = rawFlights.map((flight) => ({
            ...flight,
            id: undefined,
            startDate: toStartOfDayUtc(flight.startDate, timezone),
            endDate: toEndOfDayUtc(flight.endDate, timezone),
          }));
          delete clone.flightSchedules;

          // Scale flight schedule budgets to match the new line-item budget
          const sourceBudget = Number(sourceItem.budgetTotal) || 0;
          const cloneBudget = Number(clone.budgetTotal) || 0;
          if (sourceBudget > 0 && clone.flightSchedule.length > 0) {
            const scaleRatio = cloneBudget / sourceBudget;
            clone.flightSchedule = clone.flightSchedule.map((flight) => ({
              ...flight,
              budget: Math.round((Number(flight.budget) || 0) * scaleRatio * 100) / 100,
            }));

            // Fix rounding: ensure flights sum exactly to clone budget
            const flightsTotal = clone.flightSchedule.reduce((sum, f) => sum + (Number(f.budget) || 0), 0);
            const gap = Math.round((cloneBudget - flightsTotal) * 100) / 100;
            if (gap !== 0) {
              const lastFlight = clone.flightSchedule[clone.flightSchedule.length - 1];
              lastFlight.budget = Math.round(((Number(lastFlight.budget) || 0) + gap) * 100) / 100;
            }
          }
        }

        if (campaignStartDate) clone.startDate = campaignStartDate;
        if (campaignEndDate) clone.endDate = campaignEndDate;

        const segment = resolveSegment(blueprintItem.segment);
        if (segment) {
          clone.segments = [{ segmentId: segment.segmentId, name: segment.name, segmentType: segment.segmentType, cost: segment.cost }];
        }

        // Use the per-line-item blueprint's deals
        clone.dspDealIds = blueprint.deals.map((d) => ({
          dealId: d.dealId,
          name: d.name,
        }));

        allClones.push(clone);
      });
    });

    // Inject into form state (same pattern as Nexxen sync)
    const currentLineItems = form.getFieldValue(['campaigns', campaignIndex, 'lineItems']) || [];

    const updatedLineItems = currentLineItems.map((li, idx) => {
      const assignment = assignments[idx];
      const hadStrategyApplied = assignment && !assignment.skip && !!assignment.strategyKey;
      return {
        ...li,
        // Only mark as reference if user checked the box AND this item had a strategy applied.
        // Skipped items retain their original markedForDelivery status.
        markedForDelivery: (markOriginals && hadStrategyApplied) ? false : li.markedForDelivery,
      };
    });

    form.setFieldValue(
      ['campaigns', campaignIndex, 'lineItems'],
      [...updatedLineItems, ...allClones],
    );

    if (onFormUpdated) onFormUpdated();
    onClose();
  };

  return (
    <Modal
      title="Apply Strategy Blueprint"
      visible={visible}
      onCancel={onClose}
      width={780}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="apply"
          type="primary"
          onClick={handleApply}
          disabled={totals.totalItems === 0}
        >
          Apply Strategy ({totals.totalItems} items)
        </Button>,
      ]}
    >
      <Alert
        message="Assign a blueprint and strategy set to each line item. New items are added to the form — click Save to persist."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {originalLineItems.map((li, idx) => {
        const assignment = assignments[idx] || {};
        const budget = Number(li.budgetTotal) || 0;
        const itemBlueprint = resolveBlueprint(assignment.blueprintKey);
        const itemStrategyOptions = getOptionsForBlueprint(assignment.blueprintKey);
        const set = assignment.strategyKey && itemBlueprint ? itemBlueprint.strategySets[assignment.strategyKey] : null;
        const enabledItems = assignment.enabledItems || {};
        const enabledCount = set ? set.lineItems.filter((_, i) => enabledItems[i] !== false).length : 0;
        const itemCount = assignment.skip ? 0 : enabledCount;
        const allUnchecked = set && enabledCount === 0;

        return (
          <div
            key={idx}
            style={{
              padding: 12,
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              marginBottom: 12,
              opacity: assignment.skip ? 0.5 : 1,
            }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>{li.name || `Line Item ${idx + 1}`}</Text>
                <Text type="secondary">${budget.toLocaleString()}</Text>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Select
                  value={assignment.blueprintKey}
                  onChange={(val) => {
                    const newOpts = getOptionsForBlueprint(val);
                    const newBp = resolveBlueprint(val);
                    const newSet = newBp?.strategySets?.[newOpts[0]?.key];
                    setAssignments((prev) => ({
                      ...prev,
                      [idx]: { ...prev[idx], blueprintKey: val, strategyKey: newOpts[0]?.key || null, skip: false, enabledItems: buildEnabledItems(newSet) },
                    }));
                  }}
                  disabled={assignment.skip}
                  style={{ minWidth: 220 }}
                >
                  {ALL_BLUEPRINTS.map((bp) => (
                    <Select.Option key={bp.key} value={bp.key}>
                      {bp.label}
                    </Select.Option>
                  ))}
                </Select>
                <Select
                  value={assignment.strategyKey}
                  onChange={(val) => {
                    const bp = resolveBlueprint(assignment.blueprintKey);
                    const newSet = bp?.strategySets?.[val];
                    setAssignments((prev) => ({
                      ...prev,
                      [idx]: { ...prev[idx], strategyKey: val, skip: false, enabledItems: buildEnabledItems(newSet) },
                    }));
                  }}
                  disabled={assignment.skip}
                  style={{ minWidth: 220 }}
                >
                  {itemStrategyOptions.map((opt) => (
                    <Select.Option key={opt.key} value={opt.key}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
                <Checkbox
                  checked={assignment.skip}
                  onChange={(e) =>
                    setAssignments((prev) => ({
                      ...prev,
                      [idx]: { ...prev[idx], skip: e.target.checked },
                    }))
                  }
                >
                  Skip
                </Checkbox>
              </div>
              {!assignment.skip && set && set.lineItems.length > 0 && (
                <div style={{ borderLeft: '3px solid #1890ff', paddingLeft: 10, marginTop: 4 }}>
                  {set.lineItems.map((blueprintItem, bi) => {
                    const isEnabled = enabledItems[bi] !== false;
                    const itemBudget = isEnabled ? computeItemBudget(set, enabledItems, budget, bi) : 0;
                    return (
                      <div key={bi} style={{ display: 'flex', alignItems: 'center', padding: '2px 0' }}>
                        <Checkbox
                          checked={isEnabled}
                          onChange={() => {
                            setAssignments((prev) => ({
                              ...prev,
                              [idx]: {
                                ...prev[idx],
                                enabledItems: { ...prev[idx].enabledItems, [bi]: !isEnabled },
                              },
                            }));
                          }}
                        >
                          <span style={!isEnabled ? { opacity: 0.4 } : {}}>
                            {blueprintItem.name}
                          </span>
                        </Checkbox>
                        {isEnabled && (
                          <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                            ${itemBudget.toLocaleString()}
                          </Text>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {allUnchecked && !assignment.skip && (
                <Alert
                  message="Enable at least one item or skip this line item."
                  type="warning"
                  showIcon
                  style={{ marginTop: 4 }}
                />
              )}
              {!assignment.skip && itemCount > 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Will create {itemCount} line items totaling ${budget.toLocaleString()}
                </Text>
              )}
            </Space>
          </div>
        );
      })}

      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12, marginTop: 8 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text strong>
            Total: {totals.totalItems} items, ${totals.totalBudget.toLocaleString()}
          </Text>
          <Checkbox checked={markOriginals} onChange={(e) => setMarkOriginals(e.target.checked)}>
            Mark originals as reference-only
          </Checkbox>
        </Space>
      </div>
    </Modal>
  );
}
